/**
 * Card step queue API — continuous sessions (CONTINUOUS_SESSIONS_SPEC_v1 v1.7
 * §4.4, WP-6). One step runs on a card at a time; the rest wait in the queue.
 *
 *   GET  /api/cards/:cardId/steps/queue     who holds the card, every waiting
 *                                           step ("answer queued" for an ask
 *                                           answer, "waiting for <name>"), and
 *                                           any runtime that is not answering.
 *   POST /api/sessions/:sessionId/runtime/stop
 *                                           "Stop it to continue": a card member
 *                                           stops a runtime the server cannot
 *                                           reach; the card is free again.
 *
 * Both routes exist only while the gateway's continuous-sessions flag is on;
 * otherwise they answer the app's own not-found, which `isRouteMissing` reads
 * as "this deployment has no such feature" — callers render nothing then.
 */
import { apiJson } from './client';

export interface CardStepQueuePerson {
  userId: string | null;
  name: string | null;
}

export interface CardStepQueueItem {
  id: string;
  kind: 'step' | 'answer';
  state: string;
  reason: string | null;
  waitingFor: CardStepQueuePerson | null;
  enqueuedBy: CardStepQueuePerson;
  vendor: string | null;
  enqueuedAt: string | null;
  lastAttemptAt: string | null;
}

export interface CardStepQueue {
  cardId: string;
  inFlight: (CardStepQueuePerson & { runId: string; vendor: string | null }) | null;
  items: CardStepQueueItem[];
  fences: Array<{ sessionId: string; reason: string; since: string | null }>;
}

export const cardStepQueueApi = {
  forCard: (cardId: string) => apiJson<CardStepQueue>(`/cards/${encodeURIComponent(cardId)}/steps/queue`),
  stopRuntime: (sessionId: string) =>
    apiJson<{ ok: true; sessionId: string; outcome: string }>(
      `/sessions/${encodeURIComponent(sessionId)}/runtime/stop`,
      { method: 'POST' },
    ),
};

export const cardStepQueueKeys = {
  forCard: (cardId: string) => ['card-step-queue', cardId] as const,
};

function firstName(p: CardStepQueuePerson | null | undefined): string | null {
  const n = p?.name?.trim();
  return n ? n : null;
}

/**
 * The line the queue chip shows for one waiting item of a card under
 * continuous sessions, or null when the generic reason line already says it.
 *   "Answer queued — waiting for Alice's step to finish"
 *   "Queued — waiting for Alice's step to finish"
 *   "Answer queued — waiting for the step running on this card to finish"
 */
export function stepQueueLine(item: CardStepQueueItem): string | null {
  const who = firstName(item.waitingFor);
  const head = item.kind === 'answer' ? 'Answer queued' : 'Queued';
  if (item.reason === 'card_step_in_progress') {
    return who ? `${head} — waiting for ${who}'s step to finish` : `${head} — waiting for the step running on this card to finish`;
  }
  if (item.reason === 'card_runtime_busy') {
    return `${head} — a previous step may still be running`;
  }
  if (item.kind === 'answer') return 'Answer queued';
  return null;
}
